import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  FaYoutube,
  FaTwitter,
  FaLinkedinIn,
  FaGithub,
  FaPencilAlt,
  FaEnvelope,
  FaGraduationCap
} from 'react-icons/fa'
import Query from '../components/TeamPage/Query'
import Layout from '../components/Layout'
import Hero, { HeroTitle } from '../components/Hero'
import Link from '../components/Link'
import Image from '../components/Image'
import Markdown from '../components/Markdown'
import { getTwitterUsername } from '../helpers/url'

const Content = styled.div`
  margin: 3rem 0;
`

const Member = styled.div`
  display: inline-block;
  width: 60%;
  vertical-align: top;
  padding-right: 2rem;

  @media screen and (max-width: 899px) {
    width: 100%;
    display: block;
    padding: 0;
  }

  .member-body {
    img {
      margin: 2rem 0;
    }
  }
`

const ProfileImage = styled.div`
  display: inline-block;
  width: 100%;
  padding-left: 2rem;

  img {
    width: 100%;
  }

  @media screen and (max-width: 899px) {
    display: block;
    max-width: 30rem;
    padding: 0;
  }
`

const JobTitles = styled.ul`
  list-style: none;
  margin: 0;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.darkText};
  font-weight: 600;

  li {
    margin: 0;
  }

  &:after {
    content: '';
    border-bottom: 0.1rem solid ${({ theme }) => theme.colors.interactive};
    width: 5rem;
    display: block;
    opacity: 0.6;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
`

const Location = styled.p`
  text-transform: uppercase;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondaryText};
`

const ProfileLinks = styled.ul`
  list-style: none;
  margin: 0 0 2rem;

  li:first-of-type {
    margin-left: 0;
  }

  li:last-of-type {
    margin-right: 0;
  }
`

const TopProfile = styled.div`
  display: none;

  @media screen and (max-width: 900px) {
    display: block;
  }
`

const BottomProfile = styled.div`
  display: inline-block;
  width: 40%;

  @media screen and (max-width: 900px) {
    display: none;
  }
`

const ProfileLink = styled.li`
  display: inline-block;
  margin: 1rem;

  a {
    color: ${({ theme }) => theme.colors.secondaryText};
    font-size: 2.6rem;
    width: 6rem;
    height: 6rem;
    border-radius: 3rem;
    border: 0.1rem solid ${({ theme }) => theme.colors.secondaryText};
    transition: color 0.2s ease-in-out, border-color 0.2s ease-in-out;
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;

    svg {
      position: absolute;
      right: 50%;
      bottom: 50%;
      transform: translate(50%, 50%);
    }

    &:hover {
      color: ${({ theme }) => theme.colors.interactive};
      border-color: ${({ theme }) => theme.colors.interactive};
    }
  }
`

const Fingerprint = styled.p`
  color: ${({ theme }) => theme.colors.darkText};
  text-transform: uppercase;
  font-weight: 600;

  &:before {
    content: '';
    border-bottom: 0.1rem solid ${({ theme }) => theme.colors.interactive};
    width: 5rem;
    display: block;
    opacity: 0.6;
    margin-top: 4rem;
    margin-bottom: 2rem;
  }
`

const getHeadData = (member, content) => {
  const getDescription = () => {
    if (member.is_team_member && member.is_active)
      return `${
        member.display_name || member.title
      } - ${member.content.job_titles
        .map((jobTitle) => jobTitle.name)
        .join(', ')}`
    return `${member.display_name || member.title} - ${content.meta.profile}`
  }

  const twitterUsername = getTwitterUsername(
    member.content.profile_links.twitter
  )
  const data = {
    title: member.display_name || member.title,
    meta: [
      {
        name: 'description',
        content: getDescription()
      },
      {
        name: 'twitter:title',
        content: member.display_name || member.title
      },
      {
        name: 'twitter:description',
        content: getDescription()
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image'
      }
    ]
  }

  if (twitterUsername)
    data.meta.push({
      name: 'twitter:creator',
      content: `@${twitterUsername}`
    })

  if (member.profile_image) {
    data.meta.push({
      name: 'twitter:image',
      file: `${member.profile_image}-/resize/600/`
    })

    data.meta.push({
      name: 'twitter:image:alt',
      content: member.display_name || member.title
    })
  }

  if (!member.is_active)
    data.meta.push({
      name: 'robots',
      content: 'noindex'
    })

  return data
}

const TeamMember = ({ pageContext }) => (
  <Query
    member={pageContext.slug}
    render={({ member, content }) => (
      <Layout headData={getHeadData(member, content)}>
        <Hero>
          <HeroTitle>{content.title}</HeroTitle>
        </Hero>
        <Content>
          <Member>
            <h1>{member.display_name || member.title}</h1>
            <TopProfile>
              <ProfileImage>
                <Image
                  src={`${member.profile_image}-/quality/lighter/`}
                  alt={member.title}
                  size={200}
                />
              </ProfileImage>
            </TopProfile>
            <JobTitles>
              {member.content.job_titles.map((jobTitle) => (
                <li key={jobTitle.name}>{jobTitle.name}</li>
              ))}
            </JobTitles>
            <Location>{member.content.location}</Location>
            <ProfileLinks>
              {member.content.profile_links.youtube && (
                <ProfileLink>
                  <Link
                    tracking={{
                      label: `team_member_profile_youtube_${pageContext.slug}`
                    }}
                    href={member.content.profile_links.youtube}
                  >
                    <FaYoutube />
                  </Link>
                </ProfileLink>
              )}
              {member.is_active && member.content.profile_links.email && (
                <ProfileLink>
                  <Link
                    tracking={{
                      label: `team_member_profile_email_${pageContext.slug}`
                    }}
                    href={`mailto:${member.content.profile_links.email}`}
                  >
                    <FaEnvelope />
                  </Link>
                </ProfileLink>
              )}
              {member.content.profile_links.twitter && (
                <ProfileLink>
                  <Link
                    tracking={{
                      label: `team_member_profile_twitter_${pageContext.slug}`
                    }}
                    href={member.content.profile_links.twitter}
                  >
                    <FaTwitter />
                  </Link>
                </ProfileLink>
              )}
              {member.content.profile_links.linkedin && (
                <ProfileLink>
                  <Link
                    tracking={{
                      label: `team_member_profile_linkedin_${pageContext.slug}`
                    }}
                    href={member.content.profile_links.linkedin}
                  >
                    <FaLinkedinIn />
                  </Link>
                </ProfileLink>
              )}
              {member.content.profile_links.github && (
                <ProfileLink>
                  <Link
                    tracking={{
                      label: `team_member_profile_github_${pageContext.slug}`
                    }}
                    href={member.content.profile_links.github}
                  >
                    <FaGithub />
                  </Link>
                </ProfileLink>
              )}
              {member.content.profile_links.blog && (
                <ProfileLink>
                  <Link
                    tracking={{
                      label: `team_member_profile_blog_${pageContext.slug}`
                    }}
                    href={member.content.profile_links.blog}
                  >
                    <FaPencilAlt />
                  </Link>
                </ProfileLink>
              )}
              {member.content.profile_links.research && (
                <ProfileLink>
                  <Link
                    tracking={{
                      label: `team_member_profile_research_${pageContext.slug}`
                    }}
                    href={member.content.profile_links.research}
                  >
                    <FaGraduationCap />
                  </Link>
                </ProfileLink>
              )}
            </ProfileLinks>
            <Markdown
              className="member-body"
              source={member.content.body_text}
            />
            {member.fingerprint && (
              <Fingerprint>{member.fingerprint}</Fingerprint>
            )}
          </Member>
          <BottomProfile>
            <ProfileImage>
              <Image
                src={`${member.profile_image}-/quality/lighter/`}
                alt={member.title}
                size={600}
              />
            </ProfileImage>
          </BottomProfile>
        </Content>
      </Layout>
    )}
  />
)

TeamMember.propTypes = {
  pageContext: PropTypes.shape({
    slug: PropTypes.string.isRequired
  }).isRequired
}

export default TeamMember
